// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { getBranding, updateBranding, get2FAStatus, setup2FA, verify2FA, disable2FA, regenerateBackupCodes, changePassword } from "../lib/api";
import { Settings as SettingsIcon, Palette, Building, Globe, Save, RotateCcw, Shield, ShieldCheck, ShieldOff, Copy, AlertTriangle, Key, Eye, EyeOff } from "lucide-react";

const Settings = () => {
  const [branding, setBranding] = useState({
    company_name: "",
    trading_as: "",
    system_name: "",
    system_subtitle: "",
    address_line1: "",
    address_line2: "",
    registration_number: "",
    vat_number: "",
    contact_email: "",
    contact_phone: "",
    website: "",
    primary_color: "#1e40af",
    secondary_color: "#64748b",
    accent_color: "#0891b2"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalBranding, setOriginalBranding] = useState(null);

  // 2FA State
  const [twoFAStatus, setTwoFAStatus] = useState({ enabled: false, backup_codes_remaining: 0 });
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showBackupCodesDialog, setShowBackupCodesDialog] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [processing2FA, setProcessing2FA] = useState(false);

  // Password Change State
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const fetchBranding = useCallback(async () => {
    try {
      const response = await getBranding();
      setBranding(response.data);
      setOriginalBranding(response.data);
      setHasChanges(false);
    } catch (error) {
      toast.error("Failed to fetch branding configuration");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetch2FAStatus = useCallback(async () => {
    try {
      const response = await get2FAStatus();
      setTwoFAStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch 2FA status");
    }
  }, []);

  useEffect(() => {
    fetchBranding();
    fetch2FAStatus();
  }, [fetchBranding, fetch2FAStatus]);

  const handleChange = (field, value) => {
    if (field === 'website' && value && !value.startsWith('https://') && value.length > 0) {
      if (value.startsWith('http://')) {
        value = value.replace('http://', 'https://');
      } else if (!value.includes('://')) {
        value = 'https://' + value;
      }
    }
    setBranding(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (branding.website && !branding.website.startsWith('https://')) {
      toast.error("Website must use HTTPS for security");
      return;
    }
    
    setSaving(true);
    try {
      await updateBranding(branding);
      toast.success("Branding configuration updated successfully");
      setOriginalBranding(branding);
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update branding");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (originalBranding) {
      setBranding(originalBranding);
      setHasChanges(false);
    }
  };

  // 2FA Handlers
  const handleSetup2FA = async () => {
    setProcessing2FA(true);
    try {
      const response = await setup2FA();
      setTwoFASetup(response.data);
      setShowSetupDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate 2FA setup");
    } finally {
      setProcessing2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    if (verificationCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setProcessing2FA(true);
    try {
      const response = await verify2FA(verificationCode);
      setBackupCodes(response.data.backup_codes);
      setShowSetupDialog(false);
      setShowBackupCodesDialog(true);
      setVerificationCode("");
      fetch2FAStatus();
      toast.success("2FA enabled successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setProcessing2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    if (verificationCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setProcessing2FA(true);
    try {
      await disable2FA(verificationCode);
      setShowDisableDialog(false);
      setVerificationCode("");
      fetch2FAStatus();
      toast.success("2FA disabled successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setProcessing2FA(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (verificationCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setProcessing2FA(true);
    try {
      const response = await regenerateBackupCodes(verificationCode);
      setBackupCodes(response.data.backup_codes);
      setShowBackupCodesDialog(true);
      setVerificationCode("");
      fetch2FAStatus();
      toast.success("Backup codes regenerated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setProcessing2FA(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success("Backup codes copied to clipboard");
  };

  // Password Change Handler
  const handlePasswordChange = async () => {
    // Validation
    if (!passwordForm.currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    if (!passwordForm.newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      toast.error("New password must be different from current password");
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword({
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      toast.success("Password changed successfully");
      setShowPasswordDialog(false);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const logoUrl = "https://static.prod-images.emergentagent.com/jobs/4f9c7ad5-2823-490f-b99e-1d24db3fbcdb/images/112c92c4a088172d03acdf972c6e2e3516082b3829d18e3efd9b6298f1e5a579.png";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <SettingsIcon className="h-6 w-6 text-primary" />
            Settings
          </h1>
          <p className="text-muted-foreground">Configure branding, security, and system settings</p>
        </div>
        <div className="flex gap-2">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset} data-testid="reset-btn">
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving || !hasChanges} data-testid="save-settings-btn">
            <Save className={`h-4 w-4 mr-2 ${saving ? 'animate-pulse' : ''}`} />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <Card className="bg-card/50 backdrop-blur border-primary/20">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Two-Factor Authentication (2FA)
          </CardTitle>
          <CardDescription>Add an extra layer of security to your account</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${twoFAStatus.enabled ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
                {twoFAStatus.enabled ? (
                  <ShieldCheck className="h-6 w-6 text-emerald-400" />
                ) : (
                  <ShieldOff className="h-6 w-6 text-amber-400" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Status:</span>
                  <Badge className={twoFAStatus.enabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                    {twoFAStatus.enabled ? 'Enabled' : 'Not Enabled'}
                  </Badge>
                </div>
                {twoFAStatus.enabled && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {twoFAStatus.backup_codes_remaining} backup codes remaining
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex gap-2">
              {twoFAStatus.enabled ? (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowDisableDialog(true)}
                    data-testid="disable-2fa-btn"
                  >
                    <ShieldOff className="h-4 w-4 mr-2" />
                    Disable 2FA
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setVerificationCode("");
                      setShowBackupCodesDialog(true);
                    }}
                    data-testid="regenerate-codes-btn"
                  >
                    Regenerate Backup Codes
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={handleSetup2FA}
                  disabled={processing2FA}
                  className="bg-primary"
                  data-testid="enable-2fa-btn"
                >
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  {processing2FA ? "Setting up..." : "Enable 2FA"}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Change */}
      <Card className="bg-card/50 backdrop-blur border-primary/20">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Key className="h-5 w-5 text-primary" />
            Change Password
          </CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-primary/20">
                <Key className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-medium">Password Security</p>
                <p className="text-sm text-muted-foreground">
                  Regularly changing your password helps keep your account secure
                </p>
              </div>
            </div>
            
            <Button 
              variant="outline"
              onClick={() => setShowPasswordDialog(true)}
              data-testid="change-password-btn"
            >
              <Key className="h-4 w-4 mr-2" />
              Change Password
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logo Preview */}
      <Card className="bg-card/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Logo & Brand Identity
          </CardTitle>
          <CardDescription>Your 360Wallet logo used in reports and communications</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-32 h-32 bg-[#0a0f1a] rounded-xl flex items-center justify-center p-4">
              <img 
                src={logoUrl} 
                alt="360Wallet Logo" 
                className="w-full h-full object-contain"
                data-testid="brand-logo"
              />
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                {branding.system_name || "360Wallet"}
              </h2>
              <p className="text-muted-foreground">{branding.system_subtitle || "Authorization Engine"}</p>
              <p className="text-sm text-muted-foreground mt-2">by {branding.company_name || "Motswagae Holding"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Information */}
      <Card className="bg-card/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building className="h-5 w-5" />
            Company Information
          </CardTitle>
          <CardDescription>Legal entity details for reports and documents</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                value={branding.company_name}
                onChange={(e) => handleChange("company_name", e.target.value)}
                placeholder="Motswagae Holding (Pty) Ltd"
                data-testid="input-company-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trading_as">Trading As</Label>
              <Input
                id="trading_as"
                value={branding.trading_as}
                onChange={(e) => handleChange("trading_as", e.target.value)}
                placeholder="Motswagae Tech"
                data-testid="input-trading-as"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="system_name">System Name</Label>
              <Input
                id="system_name"
                value={branding.system_name}
                onChange={(e) => handleChange("system_name", e.target.value)}
                placeholder="360Wallet"
                data-testid="input-system-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="system_subtitle">System Subtitle</Label>
              <Input
                id="system_subtitle"
                value={branding.system_subtitle}
                onChange={(e) => handleChange("system_subtitle", e.target.value)}
                placeholder="Authorization Engine"
                data-testid="input-system-subtitle"
              />
            </div>
          </div>

          <Separator className="my-4" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="registration_number">Registration Number</Label>
              <Input
                id="registration_number"
                value={branding.registration_number}
                onChange={(e) => handleChange("registration_number", e.target.value)}
                placeholder="2020/123456/07"
                data-testid="input-reg-number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vat_number">VAT Number</Label>
              <Input
                id="vat_number"
                value={branding.vat_number}
                onChange={(e) => handleChange("vat_number", e.target.value)}
                placeholder="4123456789"
                data-testid="input-vat-number"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address_line1">Address Line 1</Label>
            <Input
              id="address_line1"
              value={branding.address_line1}
              onChange={(e) => handleChange("address_line1", e.target.value)}
              placeholder="123 Main Street"
              data-testid="input-address1"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="address_line2">Address Line 2</Label>
            <Input
              id="address_line2"
              value={branding.address_line2}
              onChange={(e) => handleChange("address_line2", e.target.value)}
              placeholder="Johannesburg, 2000"
              data-testid="input-address2"
            />
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card className="bg-card/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Contact Information
          </CardTitle>
          <CardDescription>Contact details shown in reports and notifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact_email">Contact Email</Label>
              <Input
                id="contact_email"
                type="email"
                value={branding.contact_email}
                onChange={(e) => handleChange("contact_email", e.target.value)}
                placeholder="support@360wallet.co.za"
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_phone">Contact Phone</Label>
              <Input
                id="contact_phone"
                value={branding.contact_phone}
                onChange={(e) => handleChange("contact_phone", e.target.value)}
                placeholder="+27 11 123 4567"
                data-testid="input-phone"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={branding.website}
                onChange={(e) => handleChange("website", e.target.value)}
                placeholder="https://360wallet.co.za"
                data-testid="input-website"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Color Theme */}
      <Card className="bg-card/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Color Theme
          </CardTitle>
          <CardDescription>Brand colors used in PDF reports and documents</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="primary_color">Primary Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  id="primary_color"
                  value={branding.primary_color}
                  onChange={(e) => handleChange("primary_color", e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer"
                  data-testid="color-primary"
                />
                <Input
                  value={branding.primary_color}
                  onChange={(e) => handleChange("primary_color", e.target.value)}
                  placeholder="#1e40af"
                  className="font-mono"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="secondary_color">Secondary Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  id="secondary_color"
                  value={branding.secondary_color}
                  onChange={(e) => handleChange("secondary_color", e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer"
                  data-testid="color-secondary"
                />
                <Input
                  value={branding.secondary_color}
                  onChange={(e) => handleChange("secondary_color", e.target.value)}
                  placeholder="#64748b"
                  className="font-mono"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="accent_color">Accent Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  id="accent_color"
                  value={branding.accent_color}
                  onChange={(e) => handleChange("accent_color", e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer"
                  data-testid="color-accent"
                />
                <Input
                  value={branding.accent_color}
                  onChange={(e) => handleChange("accent_color", e.target.value)}
                  placeholder="#0891b2"
                  className="font-mono"
                />
              </div>
            </div>
          </div>

          {/* Color Preview */}
          <div className="mt-6 p-4 rounded-lg bg-[#0a0f1a]">
            <p className="text-sm text-muted-foreground mb-3">Preview</p>
            <div className="flex gap-4 items-center">
              <div 
                className="w-16 h-16 rounded-lg" 
                style={{ backgroundColor: branding.primary_color }}
              />
              <div 
                className="w-16 h-16 rounded-lg" 
                style={{ backgroundColor: branding.secondary_color }}
              />
              <div 
                className="w-16 h-16 rounded-lg" 
                style={{ backgroundColor: branding.accent_color }}
              />
              <div className="flex-1">
                <h3 className="font-bold" style={{ color: branding.primary_color }}>
                  {branding.system_name || "360Wallet"}
                </h3>
                <p className="text-sm" style={{ color: branding.secondary_color }}>
                  {branding.system_subtitle || "Authorization Engine"}
                </p>
                <p className="text-xs mt-1" style={{ color: branding.accent_color }}>
                  {branding.website || "https://360wallet.co.za"}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 2FA Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Set Up Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </DialogDescription>
          </DialogHeader>
          
          {twoFASetup && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img 
                  src={twoFASetup.qr_code} 
                  alt="2FA QR Code" 
                  className="w-48 h-48 bg-white p-2 rounded-lg"
                />
              </div>
              
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Or enter this code manually:</p>
                <code className="text-sm bg-secondary px-3 py-1 rounded font-mono">
                  {twoFASetup.secret}
                </code>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Verification Code</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  className="text-center text-xl tracking-widest font-mono"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  data-testid="setup-verify-code"
                />
              </div>

              <Button 
                className="w-full" 
                onClick={handleVerify2FA}
                disabled={processing2FA || verificationCode.length !== 6}
              >
                {processing2FA ? "Verifying..." : "Verify & Enable 2FA"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="h-5 w-5" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Enter your current 2FA code to disable two-factor authentication. This will make your account less secure.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Current 2FA Code</Label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="Enter 6-digit code"
                className="text-center text-xl tracking-widest font-mono"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="disable-verify-code"
              />
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline"
                className="flex-1" 
                onClick={() => {
                  setShowDisableDialog(false);
                  setVerificationCode("");
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                className="flex-1" 
                onClick={handleDisable2FA}
                disabled={processing2FA || verificationCode.length !== 6}
              >
                {processing2FA ? "Disabling..." : "Disable 2FA"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodesDialog} onOpenChange={setShowBackupCodesDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              {backupCodes.length > 0 ? "Your Backup Codes" : "Regenerate Backup Codes"}
            </DialogTitle>
            <DialogDescription>
              {backupCodes.length > 0 
                ? "Save these codes in a secure location. Each code can only be used once."
                : "Enter your current 2FA code to generate new backup codes."
              }
            </DialogDescription>
          </DialogHeader>
          
          {backupCodes.length > 0 ? (
            <div className="space-y-4">
              <div className="bg-secondary/50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((code, index) => (
                    <code key={index} className="text-sm font-mono bg-background px-2 py-1 rounded text-center">
                      {code}
                    </code>
                  ))}
                </div>
              </div>

              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-xs text-amber-400 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  These codes will not be shown again. Save them now!
                </p>
              </div>

              <Button className="w-full" onClick={copyBackupCodes}>
                <Copy className="h-4 w-4 mr-2" />
                Copy All Codes
              </Button>

              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => {
                  setShowBackupCodesDialog(false);
                  setBackupCodes([]);
                }}
              >
                I&apos;ve Saved My Codes
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Current 2FA Code</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  className="text-center text-xl tracking-widest font-mono"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </div>

              <Button 
                className="w-full" 
                onClick={handleRegenerateBackupCodes}
                disabled={processing2FA || verificationCode.length !== 6}
              >
                {processing2FA ? "Generating..." : "Generate New Backup Codes"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={(open) => {
        setShowPasswordDialog(open);
        if (!open) {
          setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
          setShowPasswords({ current: false, new: false, confirm: false });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              Change Password
            </DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showPasswords.current ? "text" : "password"}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                  data-testid="current-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                >
                  {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPasswords.new ? "text" : "password"}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder="Enter new password (min 8 characters)"
                  data-testid="new-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                >
                  {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                >
                  {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {passwordForm.newPassword && passwordForm.confirmPassword && 
             passwordForm.newPassword !== passwordForm.confirmPassword && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-xs text-red-400 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Passwords do not match
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline"
                className="flex-1" 
                onClick={() => setShowPasswordDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1" 
                onClick={handlePasswordChange}
                disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
              >
                {changingPassword ? "Changing..." : "Change Password"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
